/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { appLocalStorage, EStorageNames } from 'helpers/storage';
import { EAnimalsFilters } from 'pages/private/Animals/components/AnimalsList/AnimalsList.type';

type TFiltersState = {
  filters: {
    [key in EStorageNames]: {
      [filter in any]?: string | string[];
    };
  };
  pagination: {
    [key in EStorageNames]: {
      page?: number;
      pageSize?: number;
    };
  };
};
const initialPaginationState = { page: 0, pageSize: 10 };

// Filter keys are persisted by name, so a renamed or retired filter lingers in localStorage and keeps
// being sent to the API. Drop anything the page no longer declares.
const KNOWN_FILTER_KEYS: { [key in EStorageNames]?: string[] } = {
  // The residency-status filter left the page, so a saved one must not keep filtering invisibly.
  [EStorageNames.ANIMALS]: Object.values(EAnimalsFilters).filter(
    key => key !== EAnimalsFilters.RESIDENCY_STATUS,
  ),
};

const pruneUnknownFilters = (
  keyStorageName: EStorageNames,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters: { [filter in any]?: string | string[] } | undefined,
) => {
  if (!filters) {
    return {};
  }
  const known = KNOWN_FILTER_KEYS[keyStorageName];
  if (!known) {
    return filters;
  }
  return Object.fromEntries(
    Object.entries(filters).filter(([filter]) => known.includes(filter)),
  );
};
// Read from storage at start-up, and again when a different admin's saved filters were removed.
const loadSavedFiltersState = (): TFiltersState => ({
  filters: {
    [EStorageNames.APPLICATIONS]:
      appLocalStorage.getItem(EStorageNames.APPLICATIONS)?.filters || {},
    [EStorageNames.VISITATIONS]:
      appLocalStorage.getItem(EStorageNames.VISITATIONS)?.filters || {},
    [EStorageNames.ANIMALS]: pruneUnknownFilters(
      EStorageNames.ANIMALS,
      appLocalStorage.getItem(EStorageNames.ANIMALS)?.filters,
    ),
    [EStorageNames.TENANTS]:
      appLocalStorage.getItem(EStorageNames.TENANTS)?.filters || {},
    [EStorageNames.LANDLORDS]: appLocalStorage.getItem(EStorageNames.LANDLORDS)
      ?.filters || { statuses: ['ACTIVE'] },
  },
  pagination: {
    [EStorageNames.APPLICATIONS]:
      appLocalStorage.getItem(EStorageNames.APPLICATIONS)?.pagination ||
      initialPaginationState,
    [EStorageNames.VISITATIONS]:
      appLocalStorage.getItem(EStorageNames.VISITATIONS)?.pagination ||
      initialPaginationState,
    [EStorageNames.ANIMALS]:
      appLocalStorage.getItem(EStorageNames.ANIMALS)?.pagination ||
      initialPaginationState,
    [EStorageNames.TENANTS]:
      appLocalStorage.getItem(EStorageNames.TENANTS)?.pagination ||
      initialPaginationState,
    [EStorageNames.LANDLORDS]:
      appLocalStorage.getItem(EStorageNames.LANDLORDS)?.pagination ||
      initialPaginationState,
  },
});

const initialState = loadSavedFiltersState();

export const filtersSlice = createSlice({
  name: 'filtersSlice',
  initialState,
  reducers: {
    reloadSavedFilters() {
      return loadSavedFiltersState();
    },
    setStoredFilters(
      state,
      action: PayloadAction<{
        keyStorageName: EStorageNames;
        filter: string;
        ids: string | string[];
      }>,
    ) {
      const newState: TFiltersState = {
        ...state,
        pagination: {
          ...state.pagination,
          [action.payload.keyStorageName]: {
            ...state.pagination[action.payload.keyStorageName],
            page: 0,
          },
        },
        filters: {
          ...state.filters,
          [action.payload.keyStorageName]: {
            ...state.filters[action.payload.keyStorageName],
            [action.payload.filter]: action.payload.ids,
          },
        },
      };
      appLocalStorage.setItem(action.payload.keyStorageName, {
        pagination: {
          ...state.pagination[action.payload.keyStorageName],
          page: 0,
        },
        filters: newState.filters[action.payload.keyStorageName],
      });
      return newState;
    },
    removeAllFilters(state) {
      const newState = state;
      Object.keys(state.filters).forEach(keyStorageName => {
        newState.filters[keyStorageName as EStorageNames] = {};
        appLocalStorage.removeItem(keyStorageName as EStorageNames);
      });
      return newState;
    },
    setPagination(
      state,
      action: PayloadAction<{
        keyStorageName: EStorageNames;
        page?: number;
        pageSize?: number;
      }>,
    ) {
      const newState: TFiltersState = {
        ...state,
        pagination: {
          ...state.pagination,
          [action.payload.keyStorageName]: {
            ...state.pagination[action.payload.keyStorageName],
            ...(action.payload.page && { page: action.payload.page }),
            ...(action.payload.pageSize && {
              pageSize: action.payload.pageSize,
            }),
          },
        },
      };
      appLocalStorage.setItem(action.payload.keyStorageName, {
        filters: state.filters[action.payload.keyStorageName],
        pagination: newState.pagination[action.payload.keyStorageName],
      });
      return newState;
    },
  },
});

export const { setStoredFilters, setPagination, removeAllFilters } =
  filtersSlice.actions;
export default filtersSlice;
